<template>
  <div v-if="!isActivityUserLoading" class="task">
    <UserTaskSelectedHeader
      :title="taskTitle"
      :current-task-number="selectedTaskIndex"
      :total-tasks="totalTasks"
      @onPreviousTask="emits('onPreviousTask')"
      @onNextTask="emits('onNextTask')"
    />

    <UserTaskSelectedStatus
      :is-done="selectedTask?.done || false"
      :is-rejected="selectedTask?.rejected || false"
      :is-certified="isCertified"
      :product-id="productId"
      :user-id="userId"
      :activity-item-user-id="
        selectedTask?.activity_item_user_id?.toString() || ''
      "
    />
    <UserTaskSelectedAttachments
      :photo="selectedTask?.photo || undefined"
      :note="selectedTask?.note || undefined"
      :is-attachments-enabled="selectedTask?.activity_attachments || false"
    />
  </div>

  <div v-else class="task">
    <div class="task-skeletons">
      <div class="task-skeletons__header">
        <BlockSkeleton size="small" width="200px" />
        <BlockSkeleton size="medium" width="70%" />
      </div>

      <div class="task-skeletons__section">
        <BlockSkeleton size="small" width="120px" />
        <BlockSkeleton size="medium" width="200px" />
      </div>

      <div class="task-skeletons__section">
        <BlockSkeleton size="small" width="10%" />
        <BlockSkeleton size="small" width="50%" />
        <BlockSkeleton size="small" width="40%" />
      </div>
      <div class="task-skeletons__section">
        <BlockSkeleton
          size="small"
          width="150px"
          height="150px"
          content-height="150px"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BlockSkeleton from '@/components/skeletons/BlockSkeleton.vue'
import { useContentLocale } from '@/hooks/useContentLocale'
import type { ActivityTask, Nullable } from '@/types'
import { computed, withDefaults } from 'vue'
import UserTaskSelectedAttachments from './UserTaskSelectedAttachments.vue'
import UserTaskSelectedHeader from './UserTaskSelectedHeader.vue'
import UserTaskSelectedStatus from './UserTaskSelectedStatus.vue'

interface Props {
  selectedTask: Nullable<ActivityTask>
  selectedTaskIndex: number
  totalTasks?: number
  productId: string
  userId?: string
  isCertified: boolean
  isActivityUserLoading: boolean
}

const props = withDefaults(defineProps<Props>(), {
  userId: '',
  totalTasks: 0,
})

const { pickTextFromLocale } = useContentLocale('-')

const taskTitle = computed(() =>
  pickTextFromLocale(props?.selectedTask?.titles || {})
)

const emits = defineEmits<{
  (e: 'onPreviousTask'): void
  (e: 'onNextTask'): void
}>()
</script>

<style lang="scss" scoped>
.task {
  @include cardStyle;
  padding: $spacing-s;
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;
  flex: 1 1 0;
  width: 100%;
}

.task-skeletons {
  display: flex;
  flex-direction: column;
  gap: $spacing-l;

  &__header {
    display: flex;
    flex-direction: column;
    gap: $spacing-4xs;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: $spacing-3xs;
  }
}
</style>
